import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-all duration-base ease-out-quart focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 focus-visible:ring-offset-bg disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-gold-500 text-bg hover:bg-gold-400 active:bg-gold-600 shadow-gold',
        secondary: 'border border-border-strong bg-bg-elevated text-fg hover:border-gold-500/40 hover:bg-bg-subtle',
        outline: 'border border-gold-500/40 bg-transparent text-gold-500 hover:border-gold-500 hover:bg-gold-500/5',
        ghost: 'text-fg hover:bg-bg-subtle',
        destructive: 'bg-danger text-bg hover:opacity-90',
        link: 'text-gold-500 underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-3 text-sm',
        md: 'h-11 px-5 text-sm',
        lg: 'h-12 px-6 text-base',
        xl: 'h-14 px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, leftIcon, rightIcon, loading, children, disabled, ...props }, ref) => {
    const classes = cn(buttonVariants({ variant, size }), className);
    const spinner = loading ? (
      <span aria-hidden className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
    ) : null;

    if (asChild) {
      // Radix Slot requires exactly one React element child. Spinner is placed
      // outside the Slot; the single child renders its own content and inherits
      // the Slot's classes + props.
      return (
        <>
          {spinner}
          <Slot ref={ref as never} className={classes} {...props}>
            {children}
          </Slot>
        </>
      );
    }

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {spinner ?? leftIcon}
        {children}
        {!loading && rightIcon}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };